import {
  AutoUpdateTimeout,
  LiveBundleIdSeparator,
  MIN_SUPPORTED_CHROME_VERSION,
  ScrollThreshold,
} from "@/constants/global";
import { useApiServicesStore } from "@/stores/core/apiServicesStore";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { useAppUpdateStore } from "@/stores/core/appUpdateStore";
import { useIonModalStore } from "@/stores/core/ionModalStore";
import { useSessionStore } from "@/stores/core/sessionStore";
import { IonFabButtonMenuItem } from "@/types/global";
import {
  contractOutline,
  desktopOutline,
  expandOutline,
  phoneLandscape,
  phonePortrait,
} from "ionicons/icons";
import { defineStore, getActivePinia, storeToRefs } from "pinia";
import { onUnmounted, ref } from "vue";

export async function setDeviceControlsMenuItems() {
  const appSettingsStore = useAppSettingsStore();
  const {
    ShowDeviceControls,
    DeviceControls,
    IsAppFullscreen,
    AppScreenOrientation,
    IsNativePlatform,
  } = storeToRefs(appSettingsStore);
  const { toggleAppScreenOrientation, toggleAppFullscreen, onEnableCastingClicked } =
    appSettingsStore;
  const fabMenuItems: Array<IonFabButtonMenuItem> = [];
  const rotateScreenIcon = AppScreenOrientation.value.startsWith("portrait")
    ? phonePortrait
    : phoneLandscape;
  if (!ShowDeviceControls.value) {
    return [];
  }
  if (DeviceControls.value.AllowScreenRotate && IsNativePlatform.value) {
    fabMenuItems.push({
      Id: 1,
      Icon: rotateScreenIcon,
      Label: "Rotate",
      onClick: async () => await toggleAppScreenOrientation(),
    });
  }
  if (DeviceControls.value.AllowFullscreen) {
    fabMenuItems.push({
      Id: 2,
      Icon: IsAppFullscreen.value ? contractOutline : expandOutline,
      Label: "Fullscreen",
      onClick: toggleAppFullscreen,
    });
  }
  // Casting hands off to the OS settings app — there is nothing to open on web.
  if (DeviceControls.value.AllowScreenCast && IsNativePlatform.value) {
    fabMenuItems.push({
      Id: 3,
      Icon: desktopOutline,
      Label: "Casting",
      onClick: onEnableCastingClicked,
    });
  }
  return fabMenuItems;
}

export async function handleAppUpdates() {
  const appSettingsStore = useAppSettingsStore();
  const { AppUpdateRequired, SkippedAppVersion, LatestAppVersion } = storeToRefs(appSettingsStore);
  if (!AppUpdateRequired.value) {
    return;
  }
  if (SkippedAppVersion.value === LatestAppVersion.value?.AppVersion) {
    return;
  }
  const appUpdateStore = useAppUpdateStore();
  const { IsLiveUpdating } = storeToRefs(appUpdateStore);
  await appUpdateStore.initializeAppUpdate();
  if (IsLiveUpdating.value) {
    const liveUpdatePromise = appUpdateStore.autoAppLiveUpdate();
    const timeoutPromise = new Promise<void>((resolve) => setTimeout(resolve, AutoUpdateTimeout));
    await Promise.race([liveUpdatePromise, timeoutPromise]);
  }
  if (!AppUpdateRequired.value) {
    return;
  }
  const { default: ModalAppUpdate } = await import("@/components/modals/shared/ModalAppUpdate.vue");
  const ionModalStore = useIonModalStore();
  const { openInlineModal } = ionModalStore;
  const modalProps = { CustomCssClass: "app-update-modal" };
  await openInlineModal(ModalAppUpdate, {}, modalProps);
}

export function useNavbarScrollHandler(
  scrollThreshold: number = ScrollThreshold,
  lockDuration: number = 300,
) {
  const appSettingsStore = useAppSettingsStore();
  const { IsTabletScreen, HideNavbarOnScroll } = storeToRefs(appSettingsStore);
  const { triggerScrollCallbacks } = appSettingsStore;
  const previousScrollTop = ref(0);
  const isTogglingNavbar = ref(false);

  let navbarToggleTimeout: NodeJS.Timeout | null = null;

  function lockNavbarToggle(): void {
    if (navbarToggleTimeout) {
      clearTimeout(navbarToggleTimeout);
    }
    isTogglingNavbar.value = true;
    navbarToggleTimeout = setTimeout(() => {
      isTogglingNavbar.value = false;
    }, lockDuration);
  }

  function handleScroll(event: any) {
    if (isTogglingNavbar.value) {
      return;
    }
    const scrollTop = event.detail.scrollTop;
    if (scrollTop < 0) {
      return;
    }
    const delta = scrollTop - previousScrollTop.value;
    if (IsTabletScreen.value) {
      if (delta < 0 && HideNavbarOnScroll.value) {
        HideNavbarOnScroll.value = false;
        lockNavbarToggle();
      } else if (delta > 0 && Math.abs(scrollTop) > scrollThreshold && !HideNavbarOnScroll.value) {
        HideNavbarOnScroll.value = true;
        lockNavbarToggle();
      }
    }
    previousScrollTop.value = scrollTop;
    triggerScrollCallbacks(event);
  }

  onUnmounted(() => {
    if (navbarToggleTimeout) {
      clearTimeout(navbarToggleTimeout);
      navbarToggleTimeout = null;
    }
  });

  return { handleScroll };
}

export function compareAppVersions(v1: string, v2: string): number {
  const parts1 = v1.split(".").map(Number);
  const parts2 = v2.split(".").map(Number);
  let versionNumber1 = 0;
  let versionNumber2 = 0;
  for (let i = 0; i < parts1.length; i++) {
    versionNumber1 += parts1[i] * Math.pow(100, 2 - i);
  }
  for (let i = 0; i < parts2.length; i++) {
    versionNumber2 += parts2[i] * Math.pow(100, 2 - i);
  }
  if (versionNumber1 === versionNumber2) return 0;
  return versionNumber1 > versionNumber2 ? 1 : -1;
}

export function isAppVersionOutdated(clientVersion: string, minVersion: string): boolean {
  return compareAppVersions(clientVersion, minVersion) < 0;
}

export function buildLiveBundleId(appVersion: string, minNativeVersion: string): string {
  return `${appVersion}${LiveBundleIdSeparator}${minNativeVersion}`;
}

export function parseLiveBundleId(bundleId: string): {
  appVersion: string;
  minNativeVersion: string | null;
} {
  const separatorIndex = bundleId.indexOf(LiveBundleIdSeparator);
  if (separatorIndex === -1) {
    return { appVersion: bundleId, minNativeVersion: null };
  }
  return {
    appVersion: bundleId.substring(0, separatorIndex),
    minNativeVersion: bundleId.substring(separatorIndex + LiveBundleIdSeparator.length),
  };
}

export async function isInternetAvailable(): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    await fetch("https://www.google.com/generate_204", {
      mode: "no-cors",
      cache: "no-store",
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return true;
  } catch {
    return false;
  }
}

// The bundle is compiled for Chrome 111 (see `build.target` in vite.config.js). Windows 7 stops
// receiving Chrome updates at 109, so those machines run below that baseline permanently and can
// fail in ways no amount of retrying fixes. Reported only so the failure screen can say so
// plainly instead of blaming the servers.
//
// Native builds are exempt: the Android WebView reports its own Chromium version in the same
// `Chrome/` token, and an old WebView is a different problem with a different fix. A browser with
// no `Chrome/` token (Firefox, Safari) is left alone rather than guessed at.
export function getUnsupportedBrowserLabel(): string | null {
  if (useAppSettingsStore().IsNativePlatform) {
    return null;
  }
  const chromeMajorVersion = /Chrome\/(\d+)/.exec(navigator.userAgent)?.[1];
  if (chromeMajorVersion === undefined) {
    return null;
  }
  const majorVersion = Number(chromeMajorVersion);
  return majorVersion < MIN_SUPPORTED_CHROME_VERSION ? `Chrome ${majorVersion}` : null;
}

export function updateLoaderText(text: string) {
  const loaderText = document.getElementById("app-loader-text");
  if (loaderText) loaderText.textContent = text;
}

export async function resetPiniaStores(isSystemExpired: boolean) {
  const excludedStores = ["appSettingsStore", "sessionStore", "apiServicesStore"];
  const appSettingsStore = useAppSettingsStore();
  const sessionStore = useSessionStore();
  const { UserInfo } = storeToRefs(sessionStore);
  const activePinia = getActivePinia();
  if (activePinia) {
    Object.entries(activePinia.state.value).forEach(([storeName, state]) => {
      if (!excludedStores.includes(storeName)) {
        const storeDefinition = defineStore(storeName, state);
        const store = storeDefinition(activePinia);
        store.$reset();
      }
    });
  }
  const apiServicesStore = useApiServicesStore();
  const { unverifiedUserApi } = storeToRefs(apiServicesStore);
  await unverifiedUserApi.value.expireLoginSession(UserInfo.value.LoginSessionId, isSystemExpired);
  await sessionStore.resetStore();
  appSettingsStore.resetMenus();
  apiServicesStore.$reset();
}
